import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import { keyframes } from '@emotion/react'

const shimmer = keyframes({
  from: { backgroundPosition: '200% 0' },
  to: { backgroundPosition: '-200% 0' },
})

export const useStyles = makeStyles()((theme: Theme) => ({
  row: {
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  },
  sparklesContainer: {
    width: 24,
    height: 24,
    borderRadius: 10,
    background:
      'linear-gradient(117.55deg, #ff708a 31.244%, #bf548e 53.577%, #733495 78.991%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  sparklesIcon: {
    fontSize: 14,
    color: theme.palette.text.white,
  },
  // The in-progress trailing line shown as plain text until its newline lands
  // (it inherits the response font from the surrounding responseBubble).
  streamingTail: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  emptyNote: {
    flex: '1 1 0',
    fontSize: 14,
    lineHeight: '20px',
    color: theme.palette.labraText.description,
    fontStyle: 'italic',
    fontFamily: 'Inter, sans-serif',
  },
  responseBubble: {
    flex: '1 1 0',
    minWidth: 0,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: theme.palette.labraText.tertiary,
    fontFamily: 'Inter, sans-serif',
    wordBreak: 'break-word',
    '& > *:first-of-type': { marginTop: 0 },
    '& > *:last-child': { marginBottom: 0 },
    '& p': { margin: '0 0 8px' },
    '& ul, & ol': { margin: '4px 0 8px', paddingLeft: 20 },
    '& li': { marginBottom: 2 },
    '& li > p': { margin: 0 },
    '& strong': { fontWeight: 600 },
    '& em': { fontStyle: 'italic' },
    '& a': {
      color: theme.palette.icon.blue,
      textDecoration: 'underline',
      wordBreak: 'break-all',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      fontWeight: 600,
      lineHeight: '20px',
      margin: '12px 0 4px',
    },
    '& h1': { fontSize: 16 },
    '& h2': { fontSize: 15 },
    '& h3, & h4, & h5, & h6': { fontSize: 14 },
    '& code': {
      fontFamily: 'monospace',
      fontSize: 12,
      backgroundColor: theme.palette.boxGradientColor.gray,
      padding: '1px 4px',
      borderRadius: 3,
    },
    '& pre': {
      backgroundColor: theme.palette.labraChat.hoverBgAlt,
      border: `1px solid ${theme.palette.labraChat.border}`,
      borderRadius: 6,
      padding: 10,
      margin: '4px 0 8px',
      overflowX: 'auto',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        fontSize: 12,
        lineHeight: '18px',
      },
    },
    '& blockquote': {
      borderLeft: `3px solid ${theme.palette.labraChat.separator}`,
      margin: '4px 0 8px',
      padding: '2px 0 2px 10px',
      color: theme.palette.labraText.description,
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.labraChat.border}`,
      margin: '8px 0',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      margin: '4px 0 8px',
      fontSize: 13,
    },
    '& th, & td': {
      border: `1px solid ${theme.palette.labraChat.border}`,
      padding: '6px 8px',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& th': {
      backgroundColor: theme.palette.labraChat.borderLight,
      fontWeight: 600,
    },
  },
  stageCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '10px 14px',
    backgroundColor: theme.palette.labraChat.hoverBg,
    borderRadius: 12,
    minWidth: 0,
  },
  stageRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    lineHeight: '18px',
    fontFamily: 'Inter, sans-serif',
  },
  stageRowDone: {
    color: theme.palette.labraText.description,
  },
  stageDoneIcon: {
    fontSize: 13,
    color: theme.palette.syncStatus.success,
    flexShrink: 0,
  },
  // Animation comes from FontAwesome's `spin` prop (the project's standard).
  stageSpinner: {
    fontSize: 14,
    color: theme.palette.labraText.description,
    flexShrink: 0,
  },
  // Active label gets a light sweep so it reads as "thinking", not static.
  stageLabelActive: {
    fontWeight: 500,
    background:
      'linear-gradient(90deg, #8a9499 0%, #2e3132 20%, #2e3132 40%, #8a9499 60%)',
    backgroundSize: '200% 100%',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    animation: `${shimmer} 1.8s linear infinite`,
  },
  // Holds the response text plus an optional active-tool row beneath it.
  contentCol: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  previewChipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  previewChip: {
    appearance: 'none' as const,
    border: 'none',
    background: '#cad9f6',
    borderRadius: 20,
    padding: '4px 12px',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: '#2e3132',
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': { backgroundColor: '#b7caf2' },
  },
  // Copy affordance pinned to the bottom-right of the message (always visible).
  copyRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  copyButton: {
    background: 'none',
    border: 'none',
    padding: 4,
    cursor: 'pointer',
    color: theme.palette.labraText.description,
    fontSize: 13,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      color: theme.palette.labraText.tertiary,
    },
  },
  // Green tick shown for 2s after a successful copy.
  copiedIcon: {
    color: theme.palette.syncStatus.success,
  },
  // Tool-in-progress row shown below already-streamed text (e.g. a tool call
  // mid-response) so the pause isn't silent.
  toolRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    lineHeight: '18px',
    fontFamily: 'Inter, sans-serif',
    marginTop: 8,
  },
}))
